import { Cable } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { BusinessDetailsView } from '@quds/shared';
import { Badge, Button, EmptyState, LoadingState, Modal } from '@/components/ui';
import { getBusinessDetails } from '@/data';
import {
  BranchApiManager,
  countConfiguredBranches,
  keysForBranch,
} from '@/features/pharmacies/api/BranchApiManager';
import type { ActionModalProps } from '../types';

/**
 * API & Integration action modal — reuses BranchApiManager (same as Access & API tab).
 * No duplicate create/reveal/rotate logic.
 */
export function ApiIntegrationModal({ pharmacy, onClose }: ActionModalProps) {
  const [details, setDetails] = useState<BusinessDetailsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = async (opts?: { background?: boolean }) => {
    // Background refresh must NOT unmount BranchApiManager (wipes one-time secrets).
    if (!opts?.background) setLoading(true);
    setLoadError(null);
    try {
      setDetails(await getBusinessDetails(pharmacy.id));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load API details');
    } finally {
      if (!opts?.background) setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [pharmacy.id]);

  const productionAvailable = details?.overview.productionAvailable === true;
  const branches = details?.cloud.branches ?? [];
  const keys = details?.api.keys ?? [];
  const apiAvailability = details?.api.availability;
  const configured = countConfiguredBranches(
    branches.map((b) => b.id),
    keys,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="API & Integration"
      description={pharmacy.name}
      icon={<Cable size={18} aria-hidden="true" />}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose} className="mr-auto">
          Close
        </Button>
      }
    >
      {loading && <LoadingState className="py-10" label="Loading API keys…" />}
      {loadError && <EmptyState title="Could not load API keys" description={loadError} />}

      {!loading && !loadError && details && (
        <div className="space-y-4">
          {!productionAvailable && <Badge tone="warning">Production temporarily unavailable — writes disabled</Badge>}

          {(apiAvailability === 'not_applicable' || apiAvailability === 'not_linked' || branches.length === 0) && (
            <EmptyState
              title="Not applicable — no cloud branch"
              description="API keys belong to production POS cloud branches. This business has no confirmed cloud branch link."
            />
          )}

          {apiAvailability === 'forbidden' && (
            <EmptyState title="API metadata restricted" description="Your role cannot view or manage API keys." />
          )}

          {apiAvailability === 'available' && branches.length > 0 && (
            <>
              <p className="text-[13px] text-ink-muted">
                Branch-scoped keys only. Branches under the same Production account share cross-branch stock visibility
                automatically.
              </p>
              <p className="text-sm text-ink-secondary">
                Configured: {configured}/{branches.length} · Active keys: {details.api.activeKeyCount}
              </p>
              <div className="space-y-3">
                {branches.map((b) => (
                  <BranchApiManager
                    key={b.id}
                    businessId={pharmacy.id}
                    branchId={b.id}
                    branchName={b.name}
                    keys={keysForBranch(keys, b.id)}
                    productionAvailable={productionAvailable}
                    onChanged={() => void reload({ background: true })}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
